/**
 * Web chat, staff side (docs/webchat.md): the visitor card of the open web chat (page, campaign, phone) and
 * "แอดมินกำลังพิมพ์…" on the visitor's screen while staff type a reply there.
 * Created by the customer tab of the side panel; the typing watcher then lives with the app.
 * Live (owner 18 ต.ค. 69 "ไม่ต้องรีเฟสหน้าเอง"): `webchat.context` (the website sent news, the e-mail was set / changed /
 * removed / unsubscribed, an e-mail notice was sent / skipped / failed) reloads the open card; `pos_order` (a POS order
 * of the chat changed, the chat was linked) reloads its "คำสั่งซื้อ" fresh — or the next time the card shows; a resync
 * (reconnect, the tab visible again) reloads the open card and its orders.
 */
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { getApi } from '../api'
import type { WebOrders, WebVisitorInfo } from '../api/types'
import { useInbox } from './inbox'

const TYPING_EVERY_MS = 3000
const REFRESH_MS = 30000
/** The website sends in bursts (a page + its cart + a message): one card reload per burst. */
const CONTEXT_RELOAD_MS = 700

export const useWebchat = defineStore('webchat', () => {
  const inbox = useInbox()
  /** conversation id → card (null = not a web chat / nothing known). */
  const visitors = ref<Record<string, WebVisitorInfo | null>>({})
  const loading = ref<Record<string, boolean>>({})
  /** conversation id → "คำสั่งซื้อของลูกค้านี้" from POS (null = not a web chat). */
  const orders = ref<Record<string, WebOrders | null>>({})
  const ordersLoading = ref<Record<string, boolean>>({})
  const ordersError = ref<Record<string, string | null>>({})
  let lastTyping: { id: string; at: number } | null = null
  let timer: ReturnType<typeof setInterval> | null = null
  const contextTimers = new Map<string, ReturnType<typeof setTimeout>>()
  /** The conversation whose card is on screen (null = none). */
  let shown: string | null = null
  /** Asked again while a load was running (the news may be newer than that answer): once more when it lands. */
  const loadAgain = new Set<string>()
  /** Same for the orders (true = one of those asks wanted a fresh POS answer). */
  const ordersAgain = new Map<string, boolean>()
  /** A POS order of the chat changed while its card was not on screen: the next load asks the POS fresh. */
  const ordersStale = new Set<string>()

  async function load(id: string): Promise<void> {
    if (loading.value[id]) {
      loadAgain.add(id)
      return
    }
    loading.value[id] = true
    try {
      visitors.value[id] = await getApi().getWebVisitor(id)
    } catch {
      /* keep what we had; the next refresh tries again */
    } finally {
      loading.value[id] = false
    }
    if (loadAgain.delete(id)) void load(id)
  }

  /** Keep the open web chat's card fresh (the visitor moves between pages, leaves, comes back). */
  function watchCard(id: string | null): void {
    if (timer) clearInterval(timer)
    timer = null
    shown = id
    if (!id) return
    listen()
    void load(id)
    timer = setInterval(() => {
      if (inbox.selectedId === id && inbox.selected?.channel === 'web') void load(id)
    }, REFRESH_MS)
  }

  function stopCard(): void {
    if (timer) clearInterval(timer)
    timer = null
    shown = null
  }

  /** POS orders of the visitor's phone / login (the API caches 60 s; `fresh` = the "โหลดใหม่" button). */
  async function loadOrders(id: string, fresh = false): Promise<void> {
    if (ordersLoading.value[id]) {
      ordersAgain.set(id, fresh || !!ordersAgain.get(id))
      return
    }
    const stale = ordersStale.delete(id)
    const asked = fresh || stale
    ordersLoading.value[id] = true
    try {
      orders.value[id] = await getApi().getWebOrders(id, asked)
      ordersError.value[id] = null
    } catch {
      ordersError.value[id] = 'โหลดคำสั่งซื้อไม่สำเร็จ ลองใหม่อีกครั้ง'
    } finally {
      ordersLoading.value[id] = false
    }
    if (ordersAgain.has(id)) {
      const next = !!ordersAgain.get(id)
      ordersAgain.delete(id)
      void loadOrders(id, next)
    }
  }

  let rtOff: (() => void) | null = null
  function listen(): void {
    if (rtOff) return
    rtOff = inbox.onRealtime((e) => {
      if (!e) {
        for (const id of Object.keys(orders.value)) if (id !== shown) ordersStale.add(id)
        if (shown) {
          void load(shown)
          if (shown in orders.value) void loadOrders(shown, true)
        }
        return
      }
      if (e.type !== 'conversation.updated' || !e.pos_order) return
      const id = e.conversation.id
      if (!(id in orders.value)) return
      if (id === shown) void loadOrders(id, true)
      else ordersStale.add(id)
    })
  }

  /** Realtime `webchat.context` (the website sent something new, the e-mail or its notice changed): reload that card if
   *  it is open. */
  function onContext(id: string): void {
    if (id !== shown && !(id in visitors.value && inbox.selectedId === id)) return
    const t = contextTimers.get(id)
    if (t) clearTimeout(t)
    contextTimers.set(id, setTimeout(() => {
      contextTimers.delete(id)
      void load(id)
    }, CONTEXT_RELOAD_MS))
  }

  // staff typing in a web chat → one ping at most every 3 s (the widget shows the dots for ~6 s)
  watch(
    () => {
      const id = inbox.selectedId
      return id ? { id, text: inbox.composers[id]?.text ?? '' } : null
    },
    (cur, prev) => {
      if (!cur || !cur.text.trim() || inbox.selected?.channel !== 'web') return
      if (prev && prev.id === cur.id && prev.text === cur.text) return
      const now = Date.now()
      if (lastTyping && lastTyping.id === cur.id && now - lastTyping.at < TYPING_EVERY_MS) return
      lastTyping = { id: cur.id, at: now }
      void getApi().sendTyping(cur.id).catch(() => undefined)
    },
  )

  return { visitors, load, watchCard, stopCard, orders, ordersLoading, ordersError, loadOrders, onContext }
})
